/* eslint-disable no-duplicate-case, no-useless-escape, unused-imports/no-unused-vars --
   Legacy switch with repeated case labels (the later copies are unreachable), escaped quotes in message
   fallbacks and unused destructured values. Left as it is on purpose: cleaning them is a separate change to a
   file that every form uses. Everything else is lint-clean (the pre-commit hook fixes it on each touch). */
// Import validation metadata from centralized file
import { VALIDATION_METADATA, HOSTNAME_REGEX } from './ValidationMetadata';
import { validatePrIpv6Format, validatePrIpv6PrefixLength, isValidPrIpv6Address } from './prIpv6Validation';

const validationRules = {
  phoneNo: {
    regex: /^[6-9]\d{9}$/,
    errMessageId: 'validationPhoneNumber'
  },
  ipMask: {
    regex:
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    errMessageId: 'validationValidSubnetMask'
  }
};
const securityCheck = /<[^>]*>/; // Security check to prevent XSS attacks

// ============================================================================
// Comprehensive IP Address Validation Utility
// ============================================================================
const ipValidation = {
  // Basic IP format validation
  basicIpRegex:
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,

  // Invalid IP patterns to exclude
  invalidPatterns: {
    regex1: /^(12[0-6]|[1-9][0-9]?|0?[1-9][0-9]?|1[0-1][0-9]|126)\.0\.0\.0$/, // [1-126].0.0.0
    regex2: /^(12[0-6]|[1-9][0-9]?|0?[1-9][0-9]?|1[0-1][0-9]|126)\.255\.255\.255$/, // [1-126].255.255.255
    regex3: /^127\.(?:[0-9]{1,3}\.){2}[0-9]{1,3}$/, // 127.x.x.x (loopback)
    regex4: /^(128|129|1[3-8][0-9]|19[0-1])\.[0-9]{1,3}\.0\.0$/, // [128-191].x.0.0
    regex5: /^(128|129|1[3-8][0-9]|19[0-1])\.[0-9]{1,3}\.255\.255$/, // [128-191].x.255.255
    regex6: /^(19[2-9]|2[01][0-9]|22[0-3])\.[0-9]{1,3}\.[0-9]{1,3}\.0$/, // [192-223].x.x.0
    regex7: /^(19[2-9]|2[01][0-9]|22[0-3])\.[0-9]{1,3}\.[0-9]{1,3}\.255$/, // [192-223].x.x.255
    regex8: /^0\.0\.0\.0$/, // 0.0.0.0
    regex9:
      /^(?:2(?:2[4-9]|[3-9][0-9]|1[0-9][0-9]|2[0-3][0-9])\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))$/ // Multicast (224.0.0.0 to 239.255.255.255)
  },

  // Specific invalid IPs
  invalidIps: ['224.0.0.1', '255.255.255.0', '255.255.255.255'],

  /**
   * Validates IP address with comprehensive checks
   * @param {string} ip - The IP address to validate
   * @returns {object} - { isValid: boolean, errorMessage: string }
   */
  validateIp: function (ip) {
    // Check if empty
    if (!ip || ip.trim() === '') {
      return { isValid: false, errorMessage: 'IP address is required' };
    }

    // Check basic IP format
    if (!this.basicIpRegex.test(ip)) {
      return { isValid: false, errorMessage: 'Invalid IP address format' };
    }

    // Check against specific invalid IPs
    if (this.invalidIps.includes(ip)) {
      return { isValid: false, errorMessage: 'This IP address is not allowed' };
    }

    // Check against invalid patterns
    for (const key in this.invalidPatterns) {
      if (this.invalidPatterns[key].test(ip)) {
        return { isValid: false, errorMessage: 'Invalid IP address range' };
      }
    }

    return { isValid: true, errorMessage: '' };
  },

  /**
   * Simple IP validation (without extensive checks)
   * @param {string} ip - The IP address to validate
   * @returns {boolean}
   */
  isValidIpBasic: function (ip) {
    return this.basicIpRegex.test(ip) && ip !== '0.0.0.0';
  }
};

// Hostname validation regex - shared canonical matcher (accepts single-label
// hostnames, underscores, consecutive hyphens and numeric/local TLDs such as ".lan2").
const hostnameRegex = HOSTNAME_REGEX;

/**
 * Validates IP address or hostname with comprehensive IP checks
 * @param {string} value - The value to validate
 * @returns {object} - { isValid: boolean, errorMessage: string }
 */
/**
 * Phase 3 R13: host validation for a ping / traceroute run where the user picked IPv6.
 *
 * Accepts an IPv6 literal or a hostname. It is a separate input type rather than a flag on
 * `ipOrHostname` because the two reject different things: with IPv6 selected, an IPv4 literal is
 * wrong, and with IPv4 selected an IPv6 literal is wrong. Keeping them apart means neither path can
 * quietly start accepting the other.
 *
 * Reuses `isValidPrIpv6Address` from prIpv6Validation (rule R3 - no feature-local regex). Note that
 * helper rejects link-local, multicast, loopback and unspecified addresses, which matches the
 * Device UI (reference/device-ui-R13-diagnostics.md 5).
 *
 * @param {string} value - the host the user typed
 * @returns {object} - { isValid: boolean, errorMessage: string }
 */
export const validateIpv6OrHostname = (value) => {
  if (!value || value.trim() === '') {
    return { isValid: false, errorMessage: 'IP address or hostname is required' };
  }

  const trimmedValue = value.trim();
  const genericError = 'Please enter a valid IPv6 address or hostname';

  // A colon can only mean an IPv6 attempt - no hostname may contain one - so once we see it the
  // value is judged as an address and never falls through to the hostname path.
  if (trimmedValue.includes(':')) {
    return isValidPrIpv6Address(trimmedValue) ? { isValid: true, errorMessage: '' } : { isValid: false, errorMessage: genericError };
  }

  // A bare IPv4 literal is a real mistake here: the user asked for IPv6 and this can never work.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(trimmedValue)) {
    return { isValid: false, errorMessage: genericError };
  }

  if (!hostnameRegex.test(trimmedValue) || trimmedValue.length > 253) {
    return { isValid: false, errorMessage: genericError };
  }
  return { isValid: true, errorMessage: '' };
};

export const validateIpOrHostname = (value) => {
  if (!value || value.trim() === '') {
    return { isValid: false, errorMessage: 'IP address or hostname is required' };
  }

  const trimmedValue = value.trim();
  const genericError = 'Please enter a valid IP address or hostname';

  // Only treat the value as an IP attempt when it resembles a full IPv4 address
  // (four dot-separated numeric octets), optionally with a malformed suffix such
  // as "192.168.1.1abc". This keeps numeric-labelled hostnames like "3152.10.lan2"
  // on the hostname path instead of failing them as bad IPs.
  const looksLikeIpAttempt = /^\d{1,3}(\.\d{1,3}){3}/.test(trimmedValue);

  if (looksLikeIpAttempt) {
    // Apply comprehensive IP validation (will catch malformed IPs)
    const ipResult = ipValidation.validateIp(trimmedValue);
    // Return generic error message instead of specific IP error
    return ipResult.isValid ? ipResult : { isValid: false, errorMessage: genericError };
  } else {
    // Validate hostname with at least one dot
    if (!hostnameRegex.test(trimmedValue)) {
      return { isValid: false, errorMessage: genericError };
    }
    if (trimmedValue.length > 253) {
      return { isValid: false, errorMessage: genericError };
    }
    return { isValid: true, errorMessage: '' };
  }
};

// Export IP validation utility for direct use in components if needed
export { ipValidation };

// Re-export VALIDATION_METADATA for use in other files
export { VALIDATION_METADATA };

// Helper function to create localized messages in any function in this file
function getLocalizedMessage(intl, messageId, fallbackMessage) {
  if (!intl) {
    return fallbackMessage; // No intl provided, use hardcoded
  }

  try {
    const localized = intl.formatMessage({ id: messageId });
    // If formatMessage returns the messageId itself, it means key doesn't exist
    // react-intl returns the key if translation is missing
    if (localized === messageId) {
      return fallbackMessage; // Key doesn't exist, use hardcoded
    }
    return localized; // Key exists, return localized message
  } catch (error) {
    return fallbackMessage; // Error occurred, use hardcoded
  }
}

const PR_IPV6_VALIDATION_CONFIG = {
  prIpv6Address: {
    kind: 'address',
    invalidMessageId: 'validationPrIpv6Address',
    invalidFallback: 'Enter a valid IPv6 address.'
  },
  prIpv6OptionalAddress: {
    kind: 'address',
    invalidMessageId: 'validationPrIpv6Address',
    invalidFallback: 'Enter a valid IPv6 address.'
  },
  prIpv6GatewayAddress: {
    kind: 'address',
    invalidMessageId: 'validationPrIpv6GatewayAddress',
    invalidFallback: 'Enter a valid IPv6 gateway address.'
  },
  prIpv6OptionalGatewayAddress: {
    kind: 'address',
    invalidMessageId: 'validationPrIpv6GatewayAddress',
    invalidFallback: 'Enter a valid IPv6 gateway address.'
  },
  prIpv6DnsAddress: {
    kind: 'address',
    invalidMessageId: 'validationPrIpv6Dns1',
    invalidFallback: 'Enter a valid primary IPv6 DNS address.'
  },
  prIpv6SecondaryDnsAddress: {
    kind: 'address',
    invalidMessageId: 'validationPrIpv6Dns2',
    invalidFallback: 'Enter a valid secondary IPv6 DNS address.'
  },
  prIpv6PrefixLength: {
    kind: 'prefix',
    invalidMessageId: 'validationPrIpv6PrefixLengthRange',
    invalidFallback: 'IPv6 prefix length must be between 1 and 128.'
  },
  prIpv6DelegatedPrefixLength: {
    kind: 'prefix',
    invalidMessageId: 'validationPrIpv6DelegatedPrefixLengthRange',
    invalidFallback: 'IPv6 delegated prefix length must be between 48 and 64.'
  },
  prIpv6OptionalDelegatedPrefixLength: {
    kind: 'prefix',
    invalidMessageId: 'validationPrIpv6DelegatedPrefixLengthRange',
    invalidFallback: 'IPv6 delegated prefix length must be between 48 and 64.'
  }
};

function validatePrIpv6Field(inputType, value, intl) {
  const config = PR_IPV6_VALIDATION_CONFIG[inputType];
  if (!config) return null;

  const rule = VALIDATION_METADATA[inputType];
  // Device UI parity (R3V-05 follow-up): the field value is checked as typed, no trim. A value of only blanks is not
  // empty there (`=== ""` / `!value`), it fails the format or the number check. The prefix-length helper reads digits like
  // `parseInt`, so blanks around the digits stay accepted there.
  const normalizedValue = value == null ? '' : String(value);

  if (!normalizedValue) {
    if (!rule?.isMandatory) {
      return [inputType, true, ''];
    }

    const requiredMessageId = rule?.errorMessages?.required;
    const requiredFallback = `${rule?.description || 'Field'} is required`;
    return [inputType, false, getLocalizedMessage(intl, requiredMessageId, requiredFallback)];
  }

  if (config.kind === 'address') {
    if (validatePrIpv6Format(normalizedValue)) {
      return [inputType, false, getLocalizedMessage(intl, config.invalidMessageId, config.invalidFallback)];
    }
    return [inputType, true, ''];
  }

  if (config.kind === 'prefix') {
    const prefixResult = validatePrIpv6PrefixLength(normalizedValue, {
      min: rule?.min,
      max: rule?.max,
      required: rule?.isMandatory
    });

    if (!prefixResult.isValid) {
      // The text is not empty here (checked above), so the helper's 'required' (blanks only, it trims) is the range message.
      return [inputType, false, getLocalizedMessage(intl, rule?.errorMessages?.range, config.invalidFallback)];
    }

    return [inputType, true, ''];
  }

  return null;
}

export function nonregexTesting(inputType, value, intl = null) {
  // ===== SPECIAL CASE: IP OR HOSTNAME WITH COMPREHENSIVE VALIDATION =====
  if (inputType === 'ipOrHostname') {
    const validation = validateIpOrHostname(value);
    if (!validation.isValid) {
      return [inputType, false, validation.errorMessage];
    }
    return [inputType, true, ''];
  }

  // ===== SPECIAL CASE: IPv6 OR HOSTNAME (Phase 3 R13, ping / traceroute with IPv6 selected) =====
  if (inputType === 'ipv6OrHostname') {
    const validation = validateIpv6OrHostname(value);
    if (!validation.isValid) {
      // Both cases are translated. The required one reuses ipAddressRequired, which the IPv4 path
      // and the submit handler already use, so clearing the box no longer shows English.
      const messageId = value && value.trim() !== '' ? 'prTroubleshootHostIpv6Invalid' : 'ipAddressRequired';
      return [inputType, false, getLocalizedMessage(intl, messageId, validation.errorMessage)];
    }
    return [inputType, true, ''];
  }

  const prIpv6Validation = validatePrIpv6Field(inputType, value, intl);
  if (prIpv6Validation) {
    return prIpv6Validation;
  }

  // ===== METADATA-BASED VALIDATION (Priority) =====
  const rule = VALIDATION_METADATA[inputType];

  if (rule) {
    // Check if mandatory and empty
    if (rule.isMandatory) {
      if (!value || value === '' || value === '0' || value === 'Choose Country') {
        const messageId = rule.errorMessages.required || rule.errorMessages.empty;
        const fallback = `${rule.description} is required`;
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
    }

    // Special handling for file uploads
    if (inputType === 'uploadImg' && value) {
      const ext = value.substring(value.lastIndexOf('.') + 1).toLowerCase();
      if (ext && !rule.allowedExtensions.includes(ext)) {
        const messageId = rule.errorMessages.invalidType;
        const fallback = 'Image only allows file types of png, jpg & JPEG';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
      return [inputType, true, ''];
    }

    // For MAC addresses, check pattern first (more informative than length error)
    if ((inputType === 'mac' || inputType === 'macAddress') && value && rule.regex) {
      if (!rule.regex.test(value)) {
        const fallback = 'Please enter a valid MAC Address.';
        return [inputType, false, getLocalizedMessage(intl, 'macValidation', fallback)];
      }
    }

    // Check minimum length
    if (value && rule.minLength !== null && value.length < rule.minLength) {
      const messageId = rule.errorMessages.minLength || rule.errorMessages.length;
      const fallback = `Minimum length is ${rule.minLength}`;
      return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
    }

    // Special handling for snmpCommunityName - check spaces before length
    if (inputType === 'snmpCommunityName' && value) {
      if (/\s/.test(value) || value.length < 1 || value.length > rule.maxLength) {
        const messageId = rule.errorMessages.maxLength || rule.errorMessages.length;
        const fallback = 'Community Name should be 1 to 64 characters in length and should not contain any spaces.';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
    }

    // Check maximum length
    if (value && rule.maxLength !== null && value.length > rule.maxLength) {
      const messageId = rule.errorMessages.maxLength || rule.errorMessages.length;
      const fallback = `Maximum length is ${rule.maxLength}`;
      return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
    }

    // RFC 5321: local-part (before @) max 64
    if (value && rule.localPartMaxLength != null) {
      const at = value.indexOf('@');
      if (at >= 0 && value.slice(0, at).length > rule.localPartMaxLength) {
        const messageId = rule.errorMessages.localPartMaxLength;
        const fallback = `The part before @ cannot exceed ${rule.localPartMaxLength} characters.`;
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
    }

    // Check numeric range (if applicable)
    if (value && rule.min !== undefined && rule.max !== undefined) {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < rule.min || numValue > rule.max) {
        const messageId = rule.errorMessages.range;
        const fallback = `Value must be between ${rule.min} and ${rule.max}`;
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
    }

    // Handle special requirements (like password complexity)
    if (value && rule.requirements) {
      const req = rule.requirements;

      if (req.noLeadingTrailingSpaces && value !== value.trim()) {
        const messageId = rule.errorMessages.noSpaces;
        const fallback = 'Password must not have leading or trailing spaces';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }

      if (req.uppercase && !/[A-Z]/.test(value)) {
        const messageId = rule.errorMessages.uppercase;
        const fallback = 'Password must contain at least one uppercase letter';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }

      if (req.lowercase && !/[a-z]/.test(value)) {
        const messageId = rule.errorMessages.lowercase;
        const fallback = 'Password must contain at least one lowercase letter';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }

      if (req.number && !/[0-9]/.test(value)) {
        const messageId = rule.errorMessages.number;
        const fallback = 'Password must contain at least one number';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }

      if (req.specialChar && !req.specialChar.test(value)) {
        const messageId = rule.errorMessages.specialChar;
        const fallback = 'Password must contain at least one special character';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
    }

    // Check regex pattern (after all other checks)
    if (value && rule.regex) {
      if (!rule.regex.test(value)) {
        const messageId = rule.errorMessages.pattern || rule.errorMessages.invalid;
        const fallback = 'Invalid format';
        return [inputType, false, messageId ? getLocalizedMessage(intl, messageId, fallback) : fallback];
      }
    }

    // All validations passed
    return [inputType, true, ''];
  }

  // ===== FALLBACK TO LEGACY SWITCH (for non-migrated cases) =====
  // Ensure intl exists to prevent crashes
  if (!intl) {
    intl = {
      formatMessage: ({ id }) => id
    };
  }
  let nameRegex = '';
  let regex = '';
  switch (inputType) {
    case 'phoneNo':
      if (value.length != 10) {
        return [inputType, false, 'error message will go here'];
      }
      break;
    case 'orgName': // For organization name
      regex = /[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'validationOrgNameEmpty' })];
      } else if (value.length > 128) {
        return [inputType, false, intl.formatMessage({ id: 'validationOrgNameLength' })];
      } else if (!nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'validationOrgNameInvalidChars' })];
      }
      break;
    case 'alphaNum': //Case for name Field
    case 'lastName':
    case 'name': //Case for name Field
    case 'onlyAlphaAndSpecial': //Case for name Field
      regex = /^[a-zA-Z0-9!"#$%&'()*+,-.\\\/:;<=>?@[\]^_`{|}~ ]+$/;
      nameRegex = regex.test(value);
      if (value) {
        if (!nameRegex) {
          return [inputType, false, 'Only alphanumeric characters and special characters !"#$%&\'()*+,-.\\/:;<=>?@[]^_`{|}~ are allowed'];
        }
      }
      break;
    case 'busiPhnNo': //Case for phone number at create/edit organization
      if (value != '') {
        // First check if it contains only numbers
        const numberOnlyRegex = /^[0-9]+$/;
        if (!numberOnlyRegex.test(value)) {
          return [inputType, false, intl.formatMessage({ id: 'validationPhoneDigitsOnly' })];
        }
        // Then check length (9-15 digits)
        if (value.length < 9 || value.length > 15) {
          return [inputType, false, intl.formatMessage({ id: 'validationPhoneLength' })];
        }
      }
      break;
    case 'uploadImg': {
      //image Validations
      // (`text` and `imgExtension` were undeclared implicit globals, a ReferenceError in a module: declared here, same logic)
      const text = value.substring(value.lastIndexOf('.') + 1);
      const imgExtension = text.toLowerCase();
      if (imgExtension != 'png' && imgExtension != 'jpg' && imgExtension != 'jpeg' && imgExtension != '') {
        return [
          inputType,
          false,
          intl
            ? intl.formatMessage({ id: 'validationImageFileTypes' })
            : getLocalizedMessage(intl, 'imageOnlyAllowsFileTypesOfPngJpgJpeg', 'Image only allows file types of png, jpg & JPEG')
        ];
      }
      break;
    }
    case 'locationName': // For Location Name
      regex = /^(?=[a-zA-Z0-9!@#$%^&()_.*-\s]{3,24}$)[^\s]+(?: +[^\s]+)*$/g;
      nameRegex = regex.test(value);
      if (value == '' || !nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'locationNameValidationMessage' })];
      }
      break;
    case 'adminPassword': // For Location Password (8-20 chars; 1 upper, 1 lower, 1 digit; allowed: a-zA-Z0-9!@#$%^&*() only)
      if (value == '' || value.length < 8 || value.length > 20) {
        return [inputType, false, intl.formatMessage({ id: 'locationPasswordValidationMessage' })];
      }
      if (value !== value.trim()) {
        return [inputType, false, intl.formatMessage({ id: 'locationPasswordValidationMessage' })];
      }
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      if (!hasUpperCase || !hasLowerCase || !hasNumber) {
        return [inputType, false, intl.formatMessage({ id: 'locationPasswordValidationMessage' })];
      }
      const allowedCharsRegex = /^[a-zA-Z0-9!@#$%^&*()]+$/;
      if (!allowedCharsRegex.test(value)) {
        return [inputType, false, intl.formatMessage({ id: 'locationPasswordValidationMessage' })];
      }
      break;
    case 'mhsAdminPassword': // MHS Device Admin Password (8-20 chars; length only)
      if (!value || value.length < 8 || value.length > 20) {
        return [
          inputType,
          false,
          intl.formatMessage({
            id: 'mhsDeviceAdminPasswordValidation',
            defaultMessage: 'Device admin password must be 8-20 characters long.'
          })
        ];
      }
      break;
    case 'onlyAlphaAndSpecial': //Case for name Field
      regex = /^[a-zA-Z0-9!"#$%&'()*+,-.\\\/:;<=>?@[\]^_`{|}~ ]+$/;
      nameRegex = regex.test(value);
      if (value) {
        if (!nameRegex) {
          return [inputType, false, 'Only alphanumeric characters and special characters !"#$%&\'()*+,-.\\/:;<=>?@[]^_`{|}~ are allowed'];
        }
      }
      break;
    case 'addressStreet':
      if (value && value.length > 100) {
        return [inputType, false, intl.formatMessage({ id: 'streetMaxLength', defaultMessage: 'Street must be at most 100 characters' })];
      }
      break;
    case 'addressCity':
      if (value && value.length > 50) {
        return [inputType, false, intl.formatMessage({ id: 'cityMaxLength', defaultMessage: 'City must be at most 50 characters' })];
      }
      break;
    case 'addressState':
      if (value && value.length > 50) {
        return [inputType, false, intl.formatMessage({ id: 'stateMaxLength', defaultMessage: 'State must be at most 50 characters' })];
      }
      break;
    case 'userName': // Case for User Name validation (required)
      if (!value || !value.trim()) {
        return [inputType, false, intl.formatMessage({ id: 'validationNameRequired' })];
      }
      break;
    case 'firstNameRequired':
      if (!value || !value.trim()) {
        return [inputType, false, intl.formatMessage({ id: 'authRegisterFirstNameCannotBeBlank' })];
      }
      break;
    case 'lastNameRequired':
      if (!value || !value.trim()) {
        return [inputType, false, intl.formatMessage({ id: 'authRegisterLastNameCannotBeBlank' })];
      }
      break;
    case 'organization':
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'validationSelectOrganization' })];
      }
      break;
    case 'role':
      if (!value) {
        return [inputType, false, intl.formatMessage({ id: 'validationSelectRole' })];
      }
      break;
    case 'postCode':
      regex = /^[0-9a-zA-Z- ]+$/; // alphanumeric regular expression
      const zipValid = regex.test(value); // check zip code is valid according to value
      if (value.length) {
        if (value.length > 15 || value.length < 3) {
          return [inputType, false, 'Zip Code must be 3 to 15 character long'];
        } else if (!zipValid) {
          // check zip code is not empty or format is not valid
          return [inputType, false, 'Invalid zip code'];
        }
      }
      //Radius Server Validation
      break;
    case 'isoCountry':
      if (value == 'Choose Country' || value == '' || value == '0') {
        return [inputType, false, 'You must specify a country'];
      }
      break;
    case 'serialNumber':
      regex = /^[123456789ABCDEFGHJKLMNPRSTUVWXYabcdefghjklmnprstuvwxy][0123456789ABCDEFGHJKLMNPRSTUVWXYabcdefghjklmnprstuvwxy]{12}$/g;
      const serialRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Serial Number cannot be blank'];
      } else if (securityCheck.test(value)) {
        // Security check to prevent XSS attacks
        return [inputType, false, 'Please do not enter any HTML or Script tags'];
      } else if (!serialRegex) {
        return [inputType, false, 'Please enter a valid Serial Number'];
      }
      break;
    case 'deviceName':
      regex = /^[a-zA-Z0-9!"#$%&'()*+,-.\\\/:;<=>?@[\]^_`{|}~ ]+$/;
      const nameRegex2 = regex.test(value);
      if (value.trim()) {
        if (securityCheck.test(value)) {
          // Security check to prevent XSS attacks
          return [inputType, false, 'Please do not enter any HTML or Script tags'];
        } else if (!nameRegex2) {
          return [inputType, false, 'Only alphanumeric characters and special characters !"#$%&\'()*+,-.\\/:;<=>?@[]^_`{|}~ are allowed'];
        }
      } else {
        return [inputType, false, 'Device Name cannot be empty'];
      }
      break;
    case 'prResDeviceName':
      regex = /^[a-zA-Z0-9]+(?:[-][a-zA-Z0-9]+)*$/;
      if (value !== '') {
        if (value.length > 63 || !regex.test(value)) {
          return [
            inputType,
            false,
            getLocalizedMessage(
              intl,
              'prResDeviceNameErr',
              'The device name should be 1–63 characters in length, alphanumeric characters and hyphens (-) are allowed. The first and last characters must be alphanumeric.'
            )
          ];
        }
      }
      break;
    case 'resDeviceName':
      if (value === '' || value.length > 32) {
        return [
          inputType,
          false,
          getLocalizedMessage(
            intl,
            'resDeviceNameErr',
            'Invalid device name. The name can have 32 characters maximum. Allowed special characters are: !@#$%^&()*'
          )
        ];
      }
      break;
    case 'macAddress':
      regex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
      const macRegex = regex.test(value);
      if (value.trim()) {
        if (!macRegex) {
          return [inputType, false, 'Enter the valid mac address'];
        }
      } else {
        return [inputType, false, 'Mac address cannot be empty'];
      }
      break;
    case 'numberOfLocation':
      const numberRegex = /^[0-9]+$/;
      if (!numberRegex.test(value)) {
        return [inputType, false, 'Please enter a valid number'];
      } else if (value <= 0 || value > 50) {
        return [inputType, false, 'Value of Number of locations can be 1-50'];
      }
      break;
    case 'imeiData':
      const imeiReg = /^[a-zA-Z0-9]{15}$/;
      if (securityCheck.test(value)) {
        // Security check to prevent XSS attacks
        return [inputType, false, 'Please do not enter any HTML or Script tags'];
      } else if (!imeiReg.test(value)) {
        return [inputType, false, 'Invalid IMEI number.'];
      }
      break;
    case 'deviceNumber': // Case for device number validation - numbers only with length constraints
      regex = /^[0-9]{6,20}$/; // Only numbers, 6-20 digits
      if (value != '') {
        const deviceNumRegex = regex.test(value);
        if (securityCheck.test(value)) {
          // Security check to prevent XSS attacks
          return [inputType, false, 'Please do not enter any HTML or Script tags'];
        } else if (!deviceNumRegex) {
          return [inputType, false, 'Device number must contain only numbers and be 6-20 digits long.'];
        }
      }
      break;

    // Network Configuration Validation Rules
    case 'ssidName': // For SSID Name validation
      regex = /^[^\/"\s]+(\s+[^\/"\s]+)*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'validationSsidNameRequired' })];
      } else if (value.length > 32 || value.length == '0') {
        return [inputType, false, intl.formatMessage({ id: 'validationSsidNameLength' })];
      } else if (!nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'validationSsidNameInvalidChars' })];
      }
      break;

    case 'ssidPassword': // For SSID Password validation
      if (value && (value.length > 63 || value.length < 8)) {
        return [inputType, false, 'SSID password must be between 8 and 63 characters'];
      }
      break;

    case 'wifiPassword': // For WiFi Password validation
      if (value && (value.length > 63 || value.length < 8)) {
        return [inputType, false, 'WiFi password must be between 8 and 63 characters'];
      }
      break;

    case 'mpskSettingsPassword': // For MPSK Settings Password validation
      if (!value || value.length > 64 || value.length < 8) {
        return [inputType, false, 'MPSK password must be between 8 and 64 characters'];
      }
      break;

    case 'vlanName': // For VLAN Name validation
      const vlanNameValid = value.length > 32;
      regex = /^(?! )^[0-9a-zA-Z-_]+( [0-9a-zA-Z-_]+)*$/;
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanNameRequired' })];
      } else if (vlanNameValid) {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanNameLength' })];
      } else if (!regex.test(value)) {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanNameFormat' })];
      }
      break;

    case 'vlanID': // For VLAN ID validation
      const numRegex = /^[0-9]+$/;
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanIdRequired' })];
      } else if (value > 4093 || value == '0' || !value.match(numRegex)) {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanIdRange' })];
      } else if (value == 3098 || value == 3099) {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanIdReserved' })];
      }
      break;

    case 'vlan-des': // For VLAN Description validation
      regex = /^[a-zA-Z0-9][a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/;
      nameRegex = regex.test(value);
      if (value) {
        if (!nameRegex) {
          return [inputType, false, intl.formatMessage({ id: 'validationVlanDescriptionFormat' })];
        }
        if (value.length >= 255) {
          return [inputType, false, intl.formatMessage({ id: 'validationVlanDescriptionLength' })];
        }
      }
      break;

    case 'ip': // For IP Address validation
    case 'ipAddress': // Alternative case for IP Address
      regex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'ipRequired', defaultMessage: 'IP address is required' })];
      } else if (!nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'enterValidIpAddress', defaultMessage: 'Enter a valid IP Address' })];
      }
      break;

    case 'subMask': // For Subnet Mask validation
    case 'subnetMask': // Enhanced subnet mask validation
      regex =
        /^(((255\.){3}(255|254|252|248|240|224|192|128|0+))|((255\.){2}(255|254|252|248|240|224|192|128|0+)\.0)|((255\.)(255|254|252|248|240|224|192|128|0+)(\.0+){2})|((255|254|252|248|240|224|192|128|0+)(\.0+){3}))$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Subnet mask is required'];
      } else if (!nameRegex || value == '0.0.0.0') {
        return [inputType, false, 'Please enter a valid subnet mask in the format 255.255.255.0'];
      }
      break;

    case 'gatewayAddress': // For Gateway Address validation
      regex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Gateway address is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'Please enter a valid gateway address'];
      }
      break;

    case 'maxClients': // For Maximum Clients validation
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '' || value > 50 || value < 1 || !nameRegex) {
        return [inputType, false, 'Maximum clients must be a number between 1 and 50'];
      }
      break;

    case 'cpURL': // For Captive Portal URL validation
      regex = /[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Captive portal URL is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'Please enter a valid URL'];
      }
      break;

    // RADIUS Server Validation
    case 'nasIdentifier': // For NAS Identifier validation
      if (value == '') {
        return [inputType, false, 'NAS Identifier is required'];
      } else if (value.length > 64) {
        return [inputType, false, 'NAS Identifier cannot exceed 64 characters'];
      }
      break;

    case 'radusPort': // For RADIUS Port validation
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'RADIUS port is required'];
      } else if (!nameRegex || value < 1 || value > 65535) {
        return [inputType, false, 'RADIUS port must be a number between 1 and 65535'];
      }
      break;

    case 'radusSecretKey': // For RADIUS Secret Key validation
      if (value == '') {
        return [inputType, false, 'RADIUS secret key is required'];
      } else if (value.length < 6 || value.length > 128) {
        return [inputType, false, 'RADIUS secret key must be between 6 and 128 characters'];
      }
      break;

    case 'secIPAddress': // For Secondary IP Address validation
      if (value != '') {
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid secondary IP address'];
        }
      }
      break;

    // LAG (Link Aggregation) Validation
    case 'lagName': // For LAG Name validation
      regex = /^[a-zA-Z0-9]{1}[a-zA-Z0-9_-]*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'LAG name is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'LAG name must start with alphanumeric character and can contain alphanumeric, underscore, and hyphen'];
      } else if (value.length > 32) {
        return [inputType, false, 'LAG name cannot exceed 32 characters'];
      }
      break;

    // SNMP Configuration Validation
    case 'snmpIpAddress': // For SNMP IP Address validation
      if (value != '') {
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid SNMP IP address'];
        }
      }
      break;

    case 'maxClients': // For Maximum Clients validation
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '' || value > 50 || value < 1 || !nameRegex) {
        return [inputType, false, 'Maximum clients must be a number between 1 and 50'];
      }
      break;

    case 'cpURL': // For Captive Portal URL validation
      regex = /[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Captive portal URL is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'Please enter a valid URL'];
      }
      break;

    // RADIUS Server Validation
    case 'nasIdentifier': // For NAS Identifier validation
      if (value == '') {
        return [inputType, false, 'NAS Identifier is required'];
      } else if (value.length > 64) {
        return [inputType, false, 'NAS Identifier cannot exceed 64 characters'];
      }
      break;

    case 'radusPort': // For RADIUS Port validation
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'RADIUS port is required'];
      } else if (!nameRegex || value < 1 || value > 65535) {
        return [inputType, false, 'RADIUS port must be a number between 1 and 65535'];
      }
      break;

    case 'radusSecretKey': // For RADIUS Secret Key validation
      if (value == '') {
        return [inputType, false, 'RADIUS secret key is required'];
      } else if (value.length < 6 || value.length > 128) {
        return [inputType, false, 'RADIUS secret key must be between 6 and 128 characters'];
      }
      break;

    case 'secIPAddress': // For Secondary IP Address validation
      if (value != '') {
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid secondary IP address'];
        }
      }
      break;

    // LAG (Link Aggregation) Validation
    case 'lagName': // For LAG Name validation
      regex = /^[a-zA-Z0-9]{1}[a-zA-Z0-9_-]*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'LAG name is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'LAG name must start with alphanumeric character and can contain alphanumeric, underscore, and hyphen'];
      } else if (value.length > 32) {
        return [inputType, false, 'LAG name cannot exceed 32 characters'];
      }
      break;

    // SNMP Configuration Validation
    case 'snmpIpAddress': // For SNMP IP Address validation
      if (value != '') {
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid SNMP IP address'];
        }
      }
      break;

    case 'snmpCommunityString': // For SNMP Community String validation
      if (value == '') {
        return [inputType, false, 'SNMP community string is required'];
      } else if (value.length > 64) {
        return [inputType, false, 'SNMP community string cannot exceed 64 characters'];
      }
      break;

    // Syslog Configuration Validation
    case 'syslogServerIp': // For Syslog Server IP validation
      if (value != '') {
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid syslog server IP address'];
        }
      }
      break;

    case 'sysLogPort': // For Syslog Port validation
      if (value != '') {
        regex = /^[0-9]+$/;
        nameRegex = regex.test(value);
        if (!nameRegex || value < 1 || value > 65535) {
          return [inputType, false, 'Syslog port must be a number between 1 and 65535'];
        }
      }
      break;

    // Custom Group Validation
    case 'customGroupName': // For Custom Group Name validation
      regex = /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Custom group name is required'];
      } else if (!nameRegex) {
        return [
          inputType,
          false,
          'Custom group name must start with alphanumeric character and can contain alphanumeric, spaces, underscores, and hyphens'
        ];
      } else if (value.length > 64) {
        return [inputType, false, 'Custom group name cannot exceed 64 characters'];
      }
      break;

    case 'customGroupDescription': // For Custom Group Description validation
      if (value == '') {
        return [inputType, false, 'Custom group description is required'];
      } else if (value.length > 512) {
        return [inputType, false, 'Custom group description cannot exceed 512 characters'];
      }
      break;

    // ORBI Specific Validations
    case 'orbiSsid': // For ORBI SSID validation
      regex = /^[^\/"\s]+(\s+[^\/"\s]+)*$/;
      nameRegex = regex.test(value);
      if (value.length === 0) {
        return [inputType, false, 'ORBI SSID name is required'];
      } else if (!nameRegex || value.length > 32) {
        return [
          inputType,
          false,
          'ORBI SSID name must be 1-32 characters and cannot contain forward slash, quotes, or leading/trailing spaces'
        ];
      }
      break;

    case 'ssidOrbiPassword': // For ORBI SSID Password validation
      regex = /^\S*$/;
      nameRegex = regex.test(value);
      if (!nameRegex || value.length > 63 || value.length < 8) {
        return [inputType, false, 'ORBI SSID password must be 8-63 characters with no spaces'];
      }
      break;

    // General Network Configuration
    case 'networkName': // For Network Name validation
      regex = /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Network name is required'];
      } else if (!nameRegex) {
        return [
          inputType,
          false,
          'Network name must start with alphanumeric character and can contain alphanumeric, spaces, underscores, and hyphens'
        ];
      } else if (value.length > 64) {
        return [inputType, false, 'Network name cannot exceed 64 characters'];
      }
      break;

    case 'password': // General password validation (different from SSID)
      generalPasswordValid = value.length >= 6;
      generalPasswordValidmax = value.length > 128;
      regex = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])[a-zA-Z0-9!@#$%^&*()]{6,128}$/;
      generalPasswordregex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Password cannot be blank'];
      } else if (!generalPasswordValid || generalPasswordValidmax || !generalPasswordregex) {
        return [
          inputType,
          false,
          'Password must be 6-128 characters with at least one uppercase, lowercase letter, and one number. Special characters allowed: !@#$%^&*()'
        ];
      }
      // Check for invalid characters
      for (let i = 0; i < value.length; i++) {
        const charCode = value.charCodeAt(i);
        if (charCode == 8221 || charCode == 34 || charCode == 63 || charCode == 92) {
          return [inputType, false, 'Password contains invalid characters'];
        }
      }
      break;

    // Port Configuration Validation
    // Unreachable: the metadata path above handles every registered type, and 'port-config-des' is
    // one. Kept in sync with the registry (anchored, 64, localized) so a reader cannot pick up the
    // stale 255 and unanchored pattern that used to sit here.
    case 'port-config-des': // For Port Configuration Description
      regex = /^[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+$/;
      nameRegex = regex.test(value);
      if (value && !nameRegex) {
        return [
          inputType,
          false,
          intl ? intl.formatMessage({ id: 'validationPortDescriptionInvalidChars' }) : 'Port description contains invalid characters'
        ];
      } else if (value && value.length > VALIDATION_METADATA['port-config-des'].maxLength) {
        return [
          inputType,
          false,
          intl ? intl.formatMessage({ id: 'validationPortDescriptionLength' }) : 'Port description cannot exceed 64 characters'
        ];
      }
      break;

    // Additional RADIUS Port Validations for specific devices
    case 'radusPortMhs': // For RADIUS Port validation (MHS devices)
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'RADIUS port is required'];
      } else if (!nameRegex || value < 1 || value > 65535) {
        return [inputType, false, 'RADIUS port must be a number between 1 and 65535'];
      }
      break;

    case 'radusPortOrbi': // For RADIUS Port validation (ORBI devices)
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'RADIUS port is required'];
      } else if (!nameRegex || value < 1 || value > 65535) {
        return [inputType, false, 'RADIUS port must be a number between 1 and 65535'];
      }
      break;

    // Email Validations
    case 'email': // For required email validation
      regex =
        /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Email is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'Please enter a valid email address'];
      }
      break;

    case 'shareEmail': // For share email validation (optional)
      regex =
        /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      nameRegex = regex.test(value);
      if (value != '' && !nameRegex) {
        return [inputType, false, 'Please enter a valid email address'];
      }
      break;

    case 'orgEmail': // For organization email (optional)
      if (value) {
        regex =
          /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid email address'];
        }
      }
      break;

    // File Upload Validations
    case 'uploadImg2': // For image upload validation (alternative case)
      ext2 = value.substring(value.lastIndexOf('.') + 1);
      uploadExtension = ext2.toLowerCase();
      if (
        uploadExtension != 'png' &&
        uploadExtension != 'jpg' &&
        uploadExtension != 'gif' &&
        uploadExtension != 'jpeg' &&
        uploadExtension != ''
      ) {
        return [inputType, false, 'Only PNG, JPG, GIF, and JPEG image formats are supported'];
      }
      break;

    case 'groupName':
      return validateDeviceGroupName(value, intl);
    case 'groupDescription':
      return validateDeviceGroupDescription(value, intl);
    // SNMP Server Validations
    case 'snmpServerValidations': // SNMP Server parameters validation (System Name, System Location, etc.)
      // regex allows Up to 64 characters, special characters, space not allowed
      regex = /^[^\s]{1,64}$/;
      if (!regex.test(value)) {
        return [inputType, false, 'Field must be 1-64 characters long and cannot contain spaces'];
      }
      break;
    case 'snmpAuthPasswordValidation': // SNMP v3 authentication password validation
      if (!value || value.length > 64 || value.length < 8) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpAuthPasswordValidationError',
          'Authentication Password should be 8 to 64 characters in length.'
        );
        return [inputType, false, errorMsg];
      }
      break;
    case 'snmpPasswordValidation': // SNMP v3 password validation (backward compatibility, maps to auth password)
      if (!value || value.length > 64 || value.length < 8) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpAuthPasswordValidationError',
          'Authentication Password should be 8 to 64 characters in length.'
        );
        return [inputType, false, errorMsg];
      }
      break;
    case 'snmpEncryptionPasswordValidation': // SNMP v3 encryption password validation
      if (!value || value.length > 64 || value.length < 8) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpEncryptionPasswordValidationError',
          'Encryption Password should be 8 to 64 characters in length.'
        );
        return [inputType, false, errorMsg];
      }
      break;
    case 'snmpSystemContactValidation': // SNMP System Contact validation
      // regex allows Up to 64 characters, special characters, space not allowed, valid email address
      // Check if value is empty or doesn't match regex pattern
      const validRegex = /^(?!.*\s)([^\s@]{1,64}@[A-Za-z0-9.-]+\.[A-Za-z]{2,64}|[^\s]{1,64})$/;
      if (!value || !value.trim() || !validRegex.test(value)) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpSystemContactValidationError',
          'System Contact must be 1-64 characters long, cannot contain spaces, and must be a valid email address or alphanumeric text'
        );
        return [inputType, false, errorMsg];
      }
      break;
    case 'snmpPortValidation': // SNMP Port validation
      const snmpRegex = /^[0-9]+$/;
      const snmpResult = snmpRegex.test(value);
      if (!snmpResult || value > 65535 || value < 1) {
        return [inputType, false, 'Port number must be between 1 and 65535'];
      }
      break;
    case 'snmpUserName': // SNMP v3 User Name validation
      if (!value || !value.trim()) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpUserNameValidationError',
          'Username should be 1 to 64 characters in length and should not contain any spaces.'
        );
        return [inputType, false, errorMsg];
      }
      // Check for length (1-64 characters) and no spaces
      if (value.length < 1 || value.length > 64) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpUserNameValidationError',
          'Username should be 1 to 64 characters in length and should not contain any spaces.'
        );
        return [inputType, false, errorMsg];
      }
      if (/\s/.test(value)) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpUserNameValidationError',
          'Username should be 1 to 64 characters in length and should not contain any spaces.'
        );
        return [inputType, false, errorMsg];
      }
      break;
    case 'snmpCommunityName': // SNMP Community Name validation
      if (!value || !value.trim()) {
        return [inputType, false, 'Community Name is required when SNMP is enabled'];
      }
      // Check for spaces or invalid length (1-64 characters)
      if (/\s/.test(value) || value.length < 1 || value.length > 64) {
        const errorMsg = getLocalizedMessage(
          intl,
          'snmpCommunityNameValidationError',
          'Community Name should be 1 to 64 characters in length and should not contain any spaces.'
        );
        return [inputType, false, errorMsg];
      }
      break;
    case 'snmpClientIpAddress': // SNMP Client IP Address validation
      if (value && value.trim() !== '') {
        const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ipRegex.test(value)) {
          return [inputType, false, 'Please enter a valid IP address'];
        }
      }
      break;
    // Backup Configuration Validation
    case 'bkupName': // For Backup Name validation (device and network level)
      if (value == '' || !value.trim()) {
        return [inputType, false, 'Backup name is required'];
      }
      const trimmedName = value.trim();
      if (trimmedName.length > 32) {
        return [inputType, false, 'Backup name cannot exceed 32 characters'];
      }
      regex = /^[a-zA-Z0-9.,\\ -]+$/;
      nameRegex = regex.test(trimmedName);
      if (!nameRegex) {
        return [inputType, false, 'Backup name can only contain letters, numbers, dots, commas, backslashes, spaces, and hyphens'];
      }
      break;
    case 'bkupDesc': // For Backup Description validation (device and network level)
      if (value == '' || !value.trim()) {
        return [inputType, false, 'Backup description is required'];
      }
      const trimmedDesc = value.trim();
      if (trimmedDesc.length > 32) {
        return [inputType, false, 'Backup description cannot exceed 32 characters'];
      }
      regex = /^[a-zA-Z0-9.,\\ -]+$/;
      nameRegex = regex.test(trimmedDesc);
      if (!nameRegex) {
        return [
          inputType,
          false,
          intl.formatMessage({
            id: 'validationBackupDescriptionInvalidChars',
            defaultMessage: 'Backup description can only contain letters, numbers, dots, commas, backslashes, spaces, and hyphens'
          })
        ];
      }
      break;

    case 'snmpCommunityString': // For SNMP Community String validation
      if (value == '') {
        return [inputType, false, 'SNMP community string is required'];
      } else if (value.length > 64) {
        return [inputType, false, 'SNMP community string cannot exceed 64 characters'];
      }
      break;

    // Syslog Configuration Validation
    case 'syslogServerIp': // For Syslog Server IP validation
      if (value != '') {
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid syslog server IP address'];
        }
      }
      break;

    case 'sysLogPort': // For Syslog Port validation
      if (value != '') {
        regex = /^[0-9]+$/;
        nameRegex = regex.test(value);
        if (!nameRegex || value < 1 || value > 65535) {
          return [inputType, false, 'Syslog port must be a number between 1 and 65535'];
        }
      }
      break;

    // Custom Group Validation
    case 'customGroupName': // For Custom Group Name validation
      regex = /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Custom group name is required'];
      } else if (!nameRegex) {
        return [
          inputType,
          false,
          'Custom group name must start with alphanumeric character and can contain alphanumeric, spaces, underscores, and hyphens'
        ];
      } else if (value.length > 64) {
        return [inputType, false, 'Custom group name cannot exceed 64 characters'];
      }
      break;

    case 'customGroupDescription': // For Custom Group Description validation
      if (value == '') {
        return [inputType, false, 'Custom group description is required'];
      } else if (value.length > 512) {
        return [inputType, false, 'Custom group description cannot exceed 512 characters'];
      }
      break;

    // ORBI Specific Validations
    case 'orbiSsid': // For ORBI SSID validation
      regex = /^[^\/"\s]+(\s+[^\/"\s]+)*$/;
      nameRegex = regex.test(value);
      if (value.length === 0) {
        return [inputType, false, 'ORBI SSID name is required'];
      } else if (!nameRegex || value.length > 32) {
        return [
          inputType,
          false,
          'ORBI SSID name must be 1-32 characters and cannot contain forward slash, quotes, or leading/trailing spaces'
        ];
      }
      break;

    case 'ssidOrbiPassword': // For ORBI SSID Password validation
      regex = /^\S*$/;
      nameRegex = regex.test(value);
      if (!nameRegex || value.length > 63 || value.length < 8) {
        return [inputType, false, 'ORBI SSID password must be 8-63 characters with no spaces'];
      }
      break;

    // General Network Configuration
    case 'networkName': // For Network Name validation
      regex = /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Network name is required'];
      } else if (!nameRegex) {
        return [
          inputType,
          false,
          'Network name must start with alphanumeric character and can contain alphanumeric, spaces, underscores, and hyphens'
        ];
      } else if (value.length > 64) {
        return [inputType, false, 'Network name cannot exceed 64 characters'];
      }
      break;

    case 'password': // General password validation (different from SSID)
      let generalPasswordValid = value.length >= 6;
      let generalPasswordValidmax = value.length > 128;
      regex = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])[a-zA-Z0-9!@#$%^&*()]{6,128}$/;
      let generalPasswordregex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Password cannot be blank'];
      } else if (!generalPasswordValid || generalPasswordValidmax || !generalPasswordregex) {
        return [
          inputType,
          false,
          'Password must be 6-128 characters with at least one uppercase, lowercase letter, and one number. Special characters allowed: !@#$%^&*()'
        ];
      }
      // Check for invalid characters
      for (let i = 0; i < value.length; i++) {
        const charCode = value.charCodeAt(i);
        if (charCode == 8221 || charCode == 34 || charCode == 63 || charCode == 92) {
          return [inputType, false, 'Password contains invalid characters'];
        }
      }
      break;

    // Port Configuration Validation
    // Unreachable: the metadata path above handles every registered type, and 'port-config-des' is
    // one. Kept in sync with the registry (anchored, 64, localized) so a reader cannot pick up the
    // stale 255 and unanchored pattern that used to sit here.
    case 'port-config-des': // For Port Configuration Description
      regex = /^[a-zA-Z0-9!"#$%&'()*+,-.\\/:;<=>?@[\]^_`{|}~ ]+$/;
      nameRegex = regex.test(value);
      if (value && !nameRegex) {
        return [
          inputType,
          false,
          intl ? intl.formatMessage({ id: 'validationPortDescriptionInvalidChars' }) : 'Port description contains invalid characters'
        ];
      } else if (value && value.length > VALIDATION_METADATA['port-config-des'].maxLength) {
        return [
          inputType,
          false,
          intl ? intl.formatMessage({ id: 'validationPortDescriptionLength' }) : 'Port description cannot exceed 64 characters'
        ];
      }
      break;

    // Additional RADIUS Port Validations for specific devices
    case 'radusPortMhs': // For RADIUS Port validation (MHS devices)
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'RADIUS port is required'];
      } else if (!nameRegex || value < 1 || value > 65535) {
        return [inputType, false, 'RADIUS port must be a number between 1 and 65535'];
      }
      break;

    case 'radusPortOrbi': // For RADIUS Port validation (ORBI devices)
      regex = /^[0-9]+$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'RADIUS port is required'];
      } else if (!nameRegex || value < 1 || value > 65535) {
        return [inputType, false, 'RADIUS port must be a number between 1 and 65535'];
      }
      break;

    // Email Validations
    case 'email': // For required email validation
      regex =
        /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, 'Email is required'];
      } else if (!nameRegex) {
        return [inputType, false, 'Please enter a valid email address'];
      }
      break;

    case 'shareEmail': // For share email validation (optional)
      regex =
        /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      nameRegex = regex.test(value);
      if (value != '' && !nameRegex) {
        return [inputType, false, 'Please enter a valid email address'];
      }
      break;

    case 'orgEmail': // For organization email (optional)
      if (value) {
        regex =
          /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        nameRegex = regex.test(value);
        if (!nameRegex) {
          return [inputType, false, 'Please enter a valid email address'];
        }
      }
      break;

    case 'notiRecipientEmail': // Notification settings recipient email (Global Settings tab)
      regex =
        /^(([^<>()[\]\\.,;!@`~#$%^&/*():?'|{}\s@\"]+(\.[^<>()[\]\\.,;!@`~#$%^&/+*():?'|{}\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!value || value.trim() === '') {
        return [inputType, false, intl.formatMessage({ id: 'notiInvalidEmail', defaultMessage: 'Please enter a valid email address.' })];
      }
      if (!regex.test(value.trim())) {
        return [inputType, false, intl.formatMessage({ id: 'notiInvalidEmail', defaultMessage: 'Please enter a valid email address.' })];
      }
      break;

    // File Upload Validations
    case 'uploadImg2': // For image upload validation (alternative case)
      let ext2 = value.substring(value.lastIndexOf('.') + 1);
      let uploadExtension = ext2.toLowerCase();
      if (
        uploadExtension != 'png' &&
        uploadExtension != 'jpg' &&
        uploadExtension != 'gif' &&
        uploadExtension != 'jpeg' &&
        uploadExtension != ''
      ) {
        return [inputType, false, 'Only PNG, JPG, GIF, and JPEG image formats are supported'];
      }
      break;

    case 'uploadBackup': // For backup file validation
      const backupExt = value.substring(value.lastIndexOf('.') + 1);
      if (backupExt == '' || (backupExt != 'zip' && backupExt != 'rar')) {
        return [inputType, false, 'Only ZIP and RAR backup formats are supported'];
      }
      break;
    case 'validateRuleName':
      const validateRuleNameReg = /^[a-zA-Z0-9 _-]{1,32}$/;
      const ruleNameCheck = validateRuleNameReg.test(value.trim());
      if (!ruleNameCheck) {
        return [
          inputType,
          false,
          intl.formatMessage({
            id: 'validationRuleNameFormat',
            defaultMessage:
              "Rule Name should contain maximum of 32 characters, only alphanumeric and special characters hyphen(-), underscore(_) and space(' ') only allowed."
          })
        ];
      }
      break;
    case 'subnetNetworkMask':
      if (value < 8 || value > 32 || value.trim() == '') {
        return [inputType, false, 'Network Mask value should be between 8 and 32.'];
      }
      break;
    case 'urlFilteringDomainValidate':
      const urlFilteringRegex = /^(http[s]?:\/\/){0,1}(www\.){0,1}([a-z0-9A-Z]+(-[a-z0-9A-Z]+)*\.)+[a-zA-Z]{2,}$/;
      if (!urlFilteringRegex.test(value)) {
        return [inputType, false, ''];
      }
      break;
    case 'groupName':
      return validateDeviceGroupName(value, intl);
    case 'groupDescription':
      return validateDeviceGroupDescription(value, intl);
    case 'ipMask': // Case for IP Mask Validation (used for subnet/netmask validation in Dual WAN Rules)
      if (value == '' || value.trim() == '') {
        return [inputType, false, intl.formatMessage({ id: 'ipMaskRequired', defaultMessage: 'Subnet mask is required' })];
      }
      nameRegex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
          value
        );
      if (!nameRegex || value == '0.0.0.0') {
        return [
          inputType,
          false,
          intl.formatMessage({ id: 'ipMaskValidation', defaultMessage: 'Please enter a valid subnet mask in the format 255.255.255.0' })
        ];
      }
      break;

    // PR WAN IP Settings Validations
    case 'vlanIdValidation': // VLAN ID validation for PR WAN (1-4094)
      const vlanIdNumRegex = /^[0-9]+$/;
      if (value == '' || !value.match(vlanIdNumRegex)) {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanIdRangeWAN' }) || 'VLAN ID must be between 1 and 4094'];
      }
      const vlanIdNum = parseInt(value, 10);
      if (vlanIdNum < 1 || vlanIdNum > 4094) {
        return [inputType, false, intl.formatMessage({ id: 'validationVlanIdRangeWAN' }) || 'VLAN ID must be between 1 and 4094'];
      }
      break;

    case 'mtuBytesValidation': // MTU bytes validation (576-1500)
      const mtuNumRegex = /^[0-9]+$/;
      if (value == '' || !mtuNumRegex.test(value)) {
        return [inputType, false, intl.formatMessage({ id: 'validationMtuRange' }) || 'MTU size must be a number between 576 and 1500'];
      }
      const mtuValue = parseInt(value, 10);
      if (mtuValue < 576 || mtuValue > 1500) {
        return [inputType, false, intl.formatMessage({ id: 'validationMtuRange' }) || 'MTU size must be between 576 and 1500 bytes'];
      }
      break;

    case 'dnsIpValidationPR': // DNS IP validation for PR (allows empty, but if provided must be valid IP)
      regex =
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      nameRegex = regex.test(value);
      if (value != '' && !nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'dnsIpValidate' }) || 'Please enter a valid DNS IP address'];
      }
      break;

    case 'hexadecimalClientString': // Client identifier validation (hexadecimal string)
      regex = /^[0-9a-fA-F]+$/;
      nameRegex = regex.test(value);
      if (value != '' && !nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'clientStringValidate' }) || 'Client identifier must be a hexadecimal string'];
      }
      break;

    case 'wanIpUsername': // PPPoE username validation (required)
      if (value == '' || value.trim() == '') {
        return [inputType, false, intl.formatMessage({ id: 'usernameRequired' }) || 'Username is required'];
      }
      break;

    case 'wanIpPassword': // PPPoE password validation (required)
      if (value == '' || value.trim() == '') {
        return [inputType, false, intl.formatMessage({ id: 'passwordBlankCommon' }) || 'Password cannot be blank'];
      }
      break;

    case 'certificateAuthorityName': // Certificate Authority Name (PR Certificate Authority)
      {
        const fieldValue = value == null ? '' : String(value);
        const regexCerti = /^[a-zA-Z0-9_]{1,64}$/;
        const isValidCerti = regexCerti.test(fieldValue);
        if (!isValidCerti) {
          if (fieldValue === '') {
            return [
              inputType,
              false,
              intl.formatMessage({
                id: 'certificateAuthorityValidation',
                defaultMessage: 'Certificate Authority Name cannot be blank.'
              })
            ];
          }
          return [
            inputType,
            false,
            intl.formatMessage({
              id: 'certificateAuthorityLength',
              defaultMessage:
                'Certificate Authority Name must be 1 to 64 characters and can contain only letters, numbers, and underscore (_).'
            })
          ];
        }
      }
      break;

    case 'commonName': // Common Name Validation (PR Certificate Authority) — trim-aware (whitespace-only is blank)
      {
        const raw = value == null ? '' : String(value);
        const trimmed = raw.trim();
        if (trimmed === '') {
          return [
            inputType,
            false,
            intl.formatMessage({
              id: 'commonNameValidation',
              defaultMessage: 'Common Name cannot be blank.'
            })
          ];
        }
        const regexcommon = /^.{1,64}$/;
        if (!regexcommon.test(trimmed)) {
          return [
            inputType,
            false,
            intl.formatMessage({
              id: 'commonNameLength',
              defaultMessage: 'Common Name must be between 1 and 64 characters.'
            })
          ];
        }
      }
      break;

    case 'alternativeNameRequired': // Subject Alternative Name (FQDN) — parity with legacy PRCertificate / UiValidate
      {
        const trimmed = value == null ? '' : String(value).trim();
        if (trimmed === '') {
          return [
            inputType,
            false,
            intl.formatMessage({
              id: 'alternativeNameRequired',
              defaultMessage: 'Alternative Name cannot be blank.'
            })
          ];
        }
      }
      break;

    case 'alternativeIPRequired': // Subject Alternative Name (IP) — parity with legacy PRCertificate / UiValidate
      {
        const ipTrimmed = value == null ? '' : String(value).trim();
        regex =
          /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        nameRegex = regex.test(ipTrimmed);
        if (ipTrimmed === '' || !ipValidation.isValidIpBasic(ipTrimmed)) {
          return [
            inputType,
            false,
            intl.formatMessage({
              id: 'alternativeIPRequired',
              defaultMessage: 'Please enter a valid IP address for Subject Alternative Name.'
            })
          ];
        }
      }
      break;

    case 'exportCertificatePassword': // Import / export certificate password (Certificate Authority)
      {
        const fieldValue = value == null ? '' : String(value);
        const trimmed = fieldValue.trim();
        // Blank = no non-whitespace content (e.g. eight spaces is not a valid password)
        if (trimmed === '') {
          return [inputType, false, intl.formatMessage({ id: 'passwordBlank', defaultMessage: 'Password cannot be blank.' })];
        }
        // Length limits apply to the raw value so intentional leading/trailing spaces still count
        const passwordValid = fieldValue.length >= 8;
        const passwordValidmax = fieldValue.length > 64;
        if (!passwordValid || passwordValidmax) {
          return [
            inputType,
            false,
            intl.formatMessage({
              id: 'exportPasswordValidation',
              defaultMessage: 'Invalid password. Password must be 8-64 characters long.'
            })
          ];
        }
      }
      break;

    case 'mac': // MAC address validation (for PR WAN and other uses)
      regex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
      nameRegex = regex.test(value);
      if (value == '') {
        return [inputType, false, intl.formatMessage({ id: 'emptyMacAddress' }) || 'MAC address cannot be empty'];
      }
      if (!nameRegex) {
        return [inputType, false, intl.formatMessage({ id: 'macValidation' }) || 'Enter a valid MAC address'];
      }
      // Normalize MAC address to uppercase for comparison (handles both colon and dash separators)
      const normalizedMac = value.toUpperCase().replace(/-/g, ':');
      if (normalizedMac == 'FF:FF:FF:FF:FF:FF' || normalizedMac == '00:00:00:00:00:00') {
        return [inputType, false, intl.formatMessage({ id: 'invalidMAC' }) || 'Invalid MAC address'];
      }
      break;

    case 'numberOfRetiresValidation': // Number of Retries validation for Dual WAN (1-10)
      const numRetriesRegex = /^[0-9]+$/;
      if (value == '' || !numRetriesRegex.test(value)) {
        return [
          inputType,
          false,
          intl.formatMessage({ id: 'validationNumberOfRetriesRange' }) || 'Number of Retries must be between 1 and 10'
        ];
      }
      const numRetries = parseInt(value, 10);
      if (numRetries < 1 || numRetries > 10) {
        return [
          inputType,
          false,
          intl.formatMessage({ id: 'validationNumberOfRetriesRange' }) || 'Number of Retries must be between 1 and 10'
        ];
      }
      break;

    case 'retryIntervalValidation': // Retry Interval validation for Dual WAN (1-3600)
      const retryIntervalRegex = /^[0-9]+$/;
      if (value == '' || !retryIntervalRegex.test(value)) {
        return [
          inputType,
          false,
          intl.formatMessage({ id: 'validationRetryIntervalRange' }) || 'Retry Interval must be between 1 and 3600 seconds'
        ];
      }
      const retryInterval = parseInt(value, 10);
      if (retryInterval < 1 || retryInterval > 3600) {
        return [
          inputType,
          false,
          intl.formatMessage({ id: 'validationRetryIntervalRange' }) || 'Retry Interval must be between 1 and 3600 seconds'
        ];
      }
      break;

    // Pro Router static route (legacy PRStaticRoute / data-type parity)
    case 'staticRouteName':
      const routeNamePattern = /^\S{1,64}$/;
      if (!routeNamePattern.test(value || '')) {
        return [
          inputType,
          false,
          intl.formatMessage({
            id: 'staticRouteNameErr',
            defaultMessage: 'Route name should be 1–64 characters in length and spaces are not allowed.'
          })
        ];
      }
      break;

    case 'staticRouteMetric':
      const metricStr = value === '' || value === undefined || value === null ? '' : String(value).trim();
      const metricNum = parseInt(metricStr, 10);
      if (metricStr === '' || !/^[0-9]+$/.test(metricStr) || Number.isNaN(metricNum) || metricNum < 1 || metricNum > 255) {
        return [
          inputType,
          false,
          intl.formatMessage({
            id: 'staticRouteMetricErr',
            defaultMessage: 'Valid Metric range is 1 to 255.'
          })
        ];
      }
      break;

    /** Legacy WireGuard / Insight `data-type="SecondaryDNSAddressSwitch"` — reuse ValidationMetadata `secondaryDNSAddress`. */
    case 'SecondaryDNSAddressSwitch':
      return nonregexTesting('secondaryDNSAddress', value, intl);
  }
  return [inputType, true, ''];
}
// If validating the from then
// type=> field/form/nonRegex
// form=> from/value(eg 10,12,"Ntegar","12:12:12:12:12"etc...)
//  dataType=> phoneNo/whatever the type of validation needed to be done.
export default function UiValidate(type = 'field', form, dataType = '', intl) {
  // Ensure intl exists at the entry point too
  if (!intl) {
    intl = {
      formatMessage: ({ id }) => id
    };
  }

  let inputType;
  let result = true;
  let message = '';
  if (type == 'form') {
    const $inputs = form.find('.formValidate:visible');
    for (let i = 0; i < $inputs.length; i++) {
      const $input = $inputs.eq(i);
      const $nativeInput = $input.find('input'); // Find the native <input> inside TextField
      const fieldType = $input.attr('data-type');
      const fieldValue = $nativeInput.val();
      inputType = fieldType.split(' ')[0];
      if (fieldType.includes('Regex')) {
        if (!validationRules[inputType]?.regex.test(fieldValue)) {
          result = false;
          return [
            inputType,
            result,
            intl ? intl.formatMessage({ id: validationRules[inputType]?.errMessageId }) : validationRules[inputType]?.errMessageId
          ];
        }
      } else {
        const [type, returnedValue, msg] = nonregexTesting(inputType, fieldValue, intl);
        if (!returnedValue) {
          result = false;
          return [type, result, msg];
        }
      }
    }
  } else {
    if (type == 'field') {
      // Check if validationRules has this dataType, otherwise use nonregexTesting
      if (validationRules[dataType] && validationRules[dataType].regex) {
        if (!validationRules[dataType].regex.test(form)) {
          result = false;
          const errMessageId = validationRules[dataType]?.errMessageId;
          message = intl && errMessageId ? intl.formatMessage({ id: errMessageId }) : errMessageId || 'Validation failed';
        }
      } else {
        // Fall back to nonregexTesting for validation types not in validationRules
        const [type, returnedValue, msg] = nonregexTesting(dataType, form, intl);
        result = returnedValue;
        message = msg;
      }
    } else {
      const [type, returnedValue, msg] = nonregexTesting(dataType, form, intl);
      result = returnedValue;
      message = msg;
    }
  }
  return [inputType, result, message];
}

export function validateDeviceName(name, deviceType, macAddress, macSupport, deviceNameLenSup, intl = null) {
  let response = {
    status: true,
    msg: ''
  };
  let errorMessage = '';
  let regex = /^[A-Za-z0-9](([A-Za-z0-9_-])*([A-Za-z0-9]))?$/g;
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
  const nameRegex = macRegex.test(macAddress);
  if (deviceType == 'AP' || deviceType == 'meshMode' || deviceType == 'ORBI' || deviceType == 'BR' || deviceType == 'PR')
    regex = /^[A-Za-z0-9](([A-Za-z0-9-])*([A-Za-z0-9]))?$/g;
  else if (deviceType == 'airBridge') regex = /^(?=.*[a-zA-Z].*)[a-zA-Z0-9]+(-+[a-zA-Z0-9]+)*$/g;

  //deviceNameLenSup will be true, if firmware support is there and name limit check will be for 64 characters
  if (securityCheck.test(name)) {
    // Security check to prevent XSS attacks
    const msg = getLocalizedMessage(intl, 'deviceNameSecurityCheck', 'Please do not enter any HTML or Script tags');
    response = {
      status: false,
      msg: msg
    };
    return response;
  } else if (deviceNameLenSup && (name.length > 63 || name.length < 2)) {
    const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween2And64', 'Device name length should be between 2-64');
    response = {
      status: false,
      msg: msg
    };
    return response;
  } else if (!deviceNameLenSup) {
    if ((deviceType == 'ORBI' || deviceType == 'meshMode') && (name.length > 15 || name.length < 2)) {
      const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween2And15', 'Device name length should be between 2-15');
      response = {
        status: false,
        msg: msg
      };
      return response;
    } else if (deviceType == 'BR' && (name.length > 15 || name.length < 1)) {
      const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween1And15', 'Device name length should be between 1-15');
      response = {
        status: false,
        msg: msg
      };
      return response;
    } else if ((deviceType == 'AP' || deviceType == 'PR') && (name.length > 64 || name.length < 2)) {
      const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween2And64', 'Device name length should be between 2-64');
      response = {
        status: false,
        msg: msg
      };
      return response;
    } else if (deviceType == 'airBridge' && (name.length > 15 || name.length < 1)) {
      const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween1And15', 'Device name length should be between 1-15');
      response = {
        status: false,
        msg: msg
      };
      return response;
    } else if (deviceType == 'MHS' && (name.length > 255 || name.length < 1)) {
      const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween1And255', 'Device name length should be between 1-255');
      response = {
        status: false,
        msg: msg
      };
      return response;
    } else if (name.length > 255 || name.length < 1) {
      const msg = getLocalizedMessage(intl, 'deviceNameLengthShouldBeBetween1And255', 'Device name length should be between 1-255');
      response = {
        status: false,
        msg: msg
      };
      return response;
    }
  }
  if (!regex.test(name)) {
    if (deviceType == 'SW')
      errorMessage = getLocalizedMessage(
        intl,
        'deviceNameInvalidSW',
        'Device name must use alphanumeric characters for the first and last characters. Rest of the device name allows either alphanumeric, underscore (_) or hyphen (-).'
      );
    else if (deviceType == 'airBridge')
      errorMessage = getLocalizedMessage(
        intl,
        'deviceNameInvalidAirBridge',
        'Invalid device name. First and last character must be alphanumeric, device name must include at least 1 alphabetic character, special symbols are not allowed except (-). Spaces are not allowed.'
      );
    else
      errorMessage = getLocalizedMessage(
        intl,
        'deviceNameMustUseAlphanumericCharacters',
        'Device name must use alphanumeric characters for the first and last characters. Rest of the device name allows either alphanumeric or hyphen (-).'
      );
    response = {
      status: false,
      msg: errorMessage
    };
    return response;
  } else if (deviceType != 'NAS' && deviceType != 'MHS' && macSupport == 1) {
    if (macAddress == '') {
      errorMessage = getLocalizedMessage(intl, 'macAddressCannotBeBlank', 'MAC Address cannot be blank');
      response = {
        status: false,
        msg: errorMessage
      };
      return response;
    }
    if (!nameRegex) {
      errorMessage = getLocalizedMessage(intl, 'macAddressFormat', 'Enter a MAC address in the AA:BB:CC:DD:EE:FF format');
      response = {
        status: false,
        msg: errorMessage
      };
      return response;
    }
    if (macAddress == 'FF:FF:FF:FF:FF:FF' || macAddress == '00:00:00:00:00:00') {
      errorMessage = getLocalizedMessage(intl, 'macAddressCannotBeF0', 'MAC Address cannot be FF:FF:FF:FF:FF:FF/00:00:00:00:00:00');
      response = {
        status: false,
        msg: errorMessage
      };
      return response;
    }
    return response;
  }
  if (macAddress == 'FF:FF:FF:FF:FF:FF' || macAddress == '00:00:00:00:00:00') {
    errorMessage = 'MAC Address cannot be FF:FF:FF:FF:FF:FF/00:00:00:00:00:00';
    response = {
      status: false,
      msg: errorMessage
    };
    return response;
  }
  return response;
}

// React-compatible showError function - now uses callback for Snackbar
export function showError(divID, msg, onShowError) {
  if (onShowError && typeof onShowError === 'function') {
    onShowError(msg, 'error');
  } else {
    // Fallback to console error if no callback provided
    console.error(`Error for ${divID}: ${msg}`);
  }
}

// React-compatible showSuccessMsg function - now uses callback for Snackbar
export function showSuccessMsg(divID, msg, onShowSuccess) {
  if (onShowSuccess && typeof onShowSuccess === 'function') {
    onShowSuccess(msg, 'success');
  } else {
    // Fallback to console log if no callback provided
  }
}

//#region Device Group Validation
export const validateDeviceGroupName = (value, intl = null) => {
  const regex = /^[a-zA-Z0-9]+$/;

  if (!value || value.trim() === '') {
    const message = getLocalizedMessage(intl, 'deviceGroupNameRequired', 'Device Group name is required');
    return [false, false, message];
  }

  if (!regex.test(value)) {
    const message = getLocalizedMessage(
      intl,
      'deviceGroupNameMustContainOnlyLettersAndNumbers',
      'Device Group name must contain only letters and numbers (no spaces or special characters)'
    );
    return [false, false, message];
  }

  if (value.length < 3) {
    const message = getLocalizedMessage(
      intl,
      'deviceGroupNameMustBeAtleast3CharactersLong',
      'Device Group name must be at least 3 characters long'
    );
    return [false, false, message];
  }

  if (value.length > 24) {
    const message = getLocalizedMessage(
      intl,
      'deviceGroupNameMustNotExceed24Characters',
      'Device Group name must not exceed 24 characters'
    );
    return [false, false, message];
  }

  return [true, true, ''];
};

export const validateDeviceGroupDescription = (value, intl = null) => {
  const regex = /^[a-zA-Z0-9!"#$%&'()*+,\-.\\/:;<=>?@[\]^_`{|}~ ]*$/;

  // Description is optional
  if (!value) {
    return [true, true, ''];
  }

  if (!regex.test(value)) {
    const message = getLocalizedMessage(intl, 'descriptionContainsInvalidCharacters', 'Description contains invalid characters');
    return [false, false, message];
  }

  if (value.length > 255) {
    const message = getLocalizedMessage(intl, 'descriptionMustNotExceed255Characters', 'Description must not exceed 255 characters');
    return [false, false, message];
  }

  // Description is optional
  if (!value) {
    return [true, true, ''];
  }

  if (!regex.test(value)) {
    return [
      false,
      false,
      intl ? intl.formatMessage({ id: 'validationDeviceGroupDescriptionInvalidChars' }) : 'validationDeviceGroupDescriptionInvalidChars'
    ];
  }

  if (value.length < 3 || value.length > 512) {
    return [
      false,
      false,
      intl ? intl.formatMessage({ id: 'validationDeviceGroupDescriptionLength' }) : 'validationDeviceGroupDescriptionLength'
    ];
  }

  return [true, true, ''];
};

//#end region Device Group Validation
